import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CarRentalIcon from '@mui/icons-material/CarRental';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { customerAPI } from '../services/api';
import { marketplaceColors } from '../styles/colors';
import './CatalogVehicleDetail.css';

const CatalogVehicleDetail = () => {
  const { catalogId, vehicleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const thumbContainerRef = useRef(null);
  const lightboxThumbRef = useRef(null);

  const fetchVehicle = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await customerAPI.getCatalogVehicle(catalogId, vehicleId);

      if (response.data.success) {
        setCatalog(response.data.data.catalog);
        setVehicle(response.data.data.vehicle);
      } else {
        setError('Failed to load vehicle details');
      }
    } catch (err) {
      console.error('Error fetching vehicle:', err);
      setError(err.response?.data?.message || 'Vehicle not found');
    } finally {
      setLoading(false);
    }
  }, [catalogId, vehicleId]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle]);

  const handleBack = () => {
    navigate(`/catalog/${catalogId}`);
  };

  const scrollToThumbnail = (index) => {
    const container = thumbContainerRef.current;
    if (container && container.children[index]) {
      container.children[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect(() => {
    scrollToThumbnail(currentImageIndex);
  }, [currentImageIndex]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#F0F6FF',
        }}
      >
        <CircularProgress size={60} sx={{ color: marketplaceColors.gold.primary }} />
      </Box>
    );
  }

  if (error || !vehicle) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: 3,
          backgroundColor: '#F0F6FF',
        }}
      >
        <Typography variant="h4" color="error" sx={{ mb: 2 }}>
          {error || 'Vehicle not found'}
        </Typography>
        <Button
          variant="contained"
          onClick={handleBack}
          sx={{
            backgroundColor: marketplaceColors.gold.primary,
            '&:hover': {
              backgroundColor: marketplaceColors.gold.dark,
            },
          }}
        >
          Back to Catalogue
        </Button>
      </Box>
    );
  }

  const images = vehicle.vehicleImageUrls || [];
  const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.variant || ''}`.trim();
  const pricePerDay = vehicle.displayPricePerDayAED || vehicle.pricePerDayAED || 0;

  const features = [
    { label: vehicle.bodyType || 'N/A', icon: <CarRentalIcon /> },
    { label: `${vehicle.seatingCapacity || 0} Seater`, icon: <PeopleIcon /> },
    { label: vehicle.transmission || 'N/A', icon: <SettingsIcon /> },
    { label: vehicle.fuelType || 'N/A', icon: <LocalGasStationIcon /> },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F6FF' }}>
      {/* Main Content */}
      <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', p: 4 }}>
        <Grid container spacing={4}>
          {/* Left Column: Image Gallery + Vehicle Details */}
          <Grid item xs={12} md={8}>
            {/* Image Gallery Card */}
            <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3, mb: 3 }}>
          <Box sx={{ position: 'relative' }}>
            {/* Back Button - Overlaid on Image */}
            <Button
              startIcon={<ArrowBackIcon sx={{ color: 'white' }} />}
              onClick={handleBack}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                color: 'white',
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                px: 2,
                py: 1,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.75)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Back to Catalogue
            </Button>

            {/* Main Image */}
            <Box
              component="img"
              src={images[currentImageIndex] || 'https://placehold.co/1200x600/cccccc/333333?text=No+Image'}
              alt={vehicleName}
              onClick={() => setLightboxOpen(true)}
              sx={{
                width: '100%',
                height: { xs: 250, sm: 350, md: 450 },
                objectFit: 'cover',
                cursor: 'pointer',
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/1200x600/cccccc/333333?text=No+Image';
              }}
            />

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <IconButton
                  onClick={() => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <ArrowBackIosIcon />
                </IconButton>
                <IconButton
                  onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}

            {/* Image Counter */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: '0.875rem',
              }}
            >
              {currentImageIndex + 1} / {images.length}
            </Box>
          </Box>

          {/* Horizontal Thumbnails */}
          {images.length > 1 && (
            <Box sx={{ p: 2, display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  pb: 1,
                  '&::-webkit-scrollbar': {
                    height: 6,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: 3,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: marketplaceColors.gold.primary,
                    borderRadius: 3,
                  },
                }}
                ref={thumbContainerRef}
              >
                {images.map((img, index) => (
                  <Box
                    key={index}
                    component="img"
                    src={img}
                    alt={`Thumbnail ${index}`}
                    sx={{
                      minWidth: 80,
                      width: 80,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: currentImageIndex === index ? `3px solid ${marketplaceColors.gold.primary}` : '2px solid transparent',
                      cursor: 'pointer',
                      opacity: currentImageIndex === index ? 1 : 0.7,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        transform: 'scale(1.05)',
                      },
                    }}
                    onClick={() => setCurrentImageIndex(index)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/80x60/cccccc/333333?text=No+Image';
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Card>

        {/* Vehicle Details Card */}
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            {vehicleName}
          </Typography>

          <Box sx={{ my: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {features.map((feature, index) => (
              <Chip
                key={index}
                icon={feature.icon}
                label={feature.label}
                sx={{
                  bgcolor: '#f8f9fa',
                  color: '#333',
                  fontWeight: 500,
                  border: '1px solid #dee2e6',
                  '&:hover': {
                    backgroundColor: marketplaceColors.gold.primary,
                    color: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${marketplaceColors.gold.primary}33`,
                  },
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>

          <Box sx={{ borderTop: 1, borderColor: '#e0e0e0', my: 3 }} />

          {/* Price */}
          {catalog.showPrices && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                AED {parseFloat(pricePerDay).toFixed(2)}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  /day
                </Typography>
              </Typography>
            </Box>
          )}

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
              DESCRIPTION
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.6 }}>
                {vehicle.description ||
                  `This ${vehicle.year} ${vehicle.make} ${vehicle.model} features a ${vehicle.fuelType} engine with ${vehicle.transmission} transmission. It comfortably seats ${vehicle.seatingCapacity} passengers and comes in ${vehicle.color} color. The vehicle has ${vehicle.odometer || 'N/A'} km on the odometer and is in ${vehicle.condition || 'Excellent'} condition.`}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: 1, borderColor: '#e0e0e0', my: 3 }} />

          {/* Vehicle Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
              VEHICLE INFORMATION
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Registration: {vehicle.registrationNumber}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Location: {vehicle.location || 'Dubai'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Customer Rating: {(parseFloat(vehicle.rating) === 0 || vehicle.ratingCount === 0) ? '5' : vehicle.rating} star{vehicle.ratingCount > 0 && ` (${vehicle.ratingCount} reviews)`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  {vehicle.serviceHistoryAvailable ? (
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : (
                    <CloseIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Service History: {vehicle.serviceHistoryAvailable ? 'Available' : 'Not Available'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  {!vehicle.accidentHistory || vehicle.accidentHistory === 'No' ? (
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : (
                    <CloseIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Accident History: {vehicle.accidentHistory || 'No'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ borderTop: 1, borderColor: '#e0e0e0', my: 3 }} />

          {/* Rental Terms */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
              RENTAL TERMS
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    Minimum Rental
                  </Typography>
                  <Typography variant="body2">{vehicle.minimumRentalDuration || 24} hours</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    KM Limit/Day
                  </Typography>
                  <Typography variant="body2">{vehicle.kmLimitPerDay || 200} km</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    Extra KM Charge
                  </Typography>
                  <Typography variant="body2">AED {vehicle.extraKmCharge || 2.0}/km</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    Security Deposit
                  </Typography>
                  <Typography variant="body2">AED {vehicle.refundableSecurityDeposit || 500.0}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>
          </Grid>

          {/* Right Column: Catalog Info Sidebar */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                position: { md: 'sticky' },
                top: 0,
                borderRadius: 3,
                p: 3,
                boxShadow: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              }}
            >
              {/* Catalog Information */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Catalogue Information
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Catalogue Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {catalog.name || 'Vehicle Catalogue'}
                </Typography>
              </Box>

              <Box sx={{ mb: 3, p: 2, backgroundColor: '#E6F2FF', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: marketplaceColors.gold.primary }}>
                  View-Only Catalogue
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This is a showcase catalogue
                </Typography>
              </Box>

              {catalog.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {catalog.description}
                  </Typography>
                </Box>
              )}

            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Image Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            outline: 'none',
          }}
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
              zIndex: 10,
            }}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>

          {/* Main Lightbox Image */}
          <Box
            component="img"
            src={images[currentImageIndex] || 'https://placehold.co/1200x600/cccccc/333333?text=No+Image'}
            alt={vehicleName}
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: '85%',
              maxHeight: '70vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/1200x600/cccccc/333333?text=No+Image';
            }}
          />

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
                }}
                sx={{
                  position: 'absolute',
                  left: { xs: 8, sm: 24 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                }}
              >
                <ArrowBackIosIcon />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev + 1) % images.length);
                }}
                sx={{
                  position: 'absolute',
                  right: { xs: 8, sm: 24 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}

          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'absolute',
                bottom: 24,
                display: 'flex',
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                borderRadius: 2,
                maxWidth: '90%',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: 2,
                },
              }}
              ref={lightboxThumbRef}
            >
              {images.map((img, index) => (
                <Box
                  key={index}
                  component="img"
                  src={img}
                  alt={`Thumbnail ${index}`}
                  sx={{
                    minWidth: 60,
                    width: 60,
                    height: 45,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: currentImageIndex === index
                      ? `2px solid ${marketplaceColors.gold.primary}`
                      : '2px solid transparent',
                    cursor: 'pointer',
                    opacity: currentImageIndex === index ? 1 : 0.6,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                  onClick={() => setCurrentImageIndex(index)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/60x45/cccccc/333333?text=No';
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Modal>

      {/* Footer */}
      <Box
        sx={{
          textAlign: 'center',
          padding: 3,
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #e0e0e0',
          mt: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <img src="/logo.png" alt="Xoom Drives" style={{ height: 24 }} />
        </Typography>
      </Box>
    </Box>
  );
};

export default CatalogVehicleDetail;
